import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12] [CELL-PRESS 2026-10-13]
import styled from 'styled-components';
import { fetchRefunds, fetchRefundFilters, deleteRefund, updateRefundField, peekCache, monthsParams } from '../services/api';
import { useRealtime } from '../hooks/useRealtime'; // [REALTIME] 2026-06-23
import { Refund, PaginationMeta, SortConfig, RefundFiltersResponse, Column } from '../types';
import SearchBar from '../components/SearchBar';
import DataTable from '../components/DataTable';
import { TableSkeleton } from '../components/Skeleton';
import { LoadFailed, EmptyState } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import ConfirmDialog from '../components/ConfirmDialog';
import { RowTagBadge } from '../components/RowTagBadge'; // [RD-ROW-TAG 2026-10-01]
import { lgPress, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { alertDialog } from '../components/dialogService';
import { toastSuccess } from '../components/toastService';
import GlassSelect from '../components/GlassSelect';
import SectionSwitcher from '../components/SectionSwitcher';
import { latestDataMonth } from '../utils/monthFilter';
import { effectiveCurrentMonth } from '../utils/effectiveMonth';
import { useAuth } from '../contexts/AuthContext';

/* [PERF 2026-08-14] Locale formatters built ONCE — `toLocale*(locale, {options})`
 * constructs a fresh Intl formatter per call (measured 34us money / 90us date on V8,
 * vs 0.7us / 1.5us cached). Calls WITHOUT options are already fast and left alone. */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTH_LONG_YEAR_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
// [DATE-STYLE 2026-09-29, user] "9/24/2026" → "Sep 24, 2026" (one date style across the app).
const MONTH_SHORT_DAY_YEAR_UTC_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const MONTH_SHORT_YEAR_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });

import { useRestorableState } from '../services/uiSnapshot'; // [SEAMLESS-UPDATE 2026-08-13]
import { useIsMobile } from '../hooks/useIsMobile'; // [MOBILE 2026-07-07]
import { useHubSpotRoster, CaseTeam } from '../hooks/useHubSpotRoster'; // [CASE-ASSIGN 2026-08-13]


/* ── Styled components ── */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* [STICKY-HEAD 2026-06-26] Fill the scroll card exactly and don't scroll HERE — the
     toolbar + column header stay frozen and only the table rows (below) scroll. */
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: ${(p) => p.theme.surface};

  /* [PHONE-LAYOUT 2026-10-23, user] phones: NOTHING is fixed at the top — the Refunds | Disputes switch, the search, the
     filters and the table are ONE scroll ("in the phone view nothing will be fixed on the top side otherwise how will i
     see the data for the below"). The table only swipes sideways (ScrollContainer below). */
  @media (max-width: 768px) {
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

// [STICKY-HEAD 2026-06-26] The FIXED top block — the toolbar (title + search + filters). It
// sits OUTSIDE the scrolling table area (flex-shrink:0) so it never scrolls; only the table
// rows below it do. The column header itself freezes via the DataTable's sticky <thead>
// (parentScroll) pinned to the top of the scroll area below.
const FrozenTop = styled.div`
  flex-shrink: 0;
  background: ${(p) => p.theme.surface};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};
  flex-wrap: wrap;
  gap: 10px;

  /* Phones: stack title / search / filters vertically (each full-width). The
     Filters row already wraps. Desktop layout is unchanged. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 10px 12px;
  }
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  /* [MOBILE 2026-07-07] Phones: the three filter dropdowns share ONE row as an
     equal 3-column grid (they sit on their own row below the search — see the
     render branch). min-width:0 lets the glass triggers shrink below their
     130px desktop floor so all three fit the phone width. */
  /* [PHONE-LAYOUT 2026-10-22] Two per row (three cut "October 2026" / "All Sales/PM" to "Octo..." at 360-390 px);
     an odd last dropdown spans the whole row, so every label shows in full. */
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
    gap: 8px;
    & > button { min-width: 0; width: 100%; }
    & > :last-child:nth-child(odd) { grid-column: 1 / -1; }
  }
`;

const RecordCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
`;

// [MOBILE 2026-07-07] Phones only: the search field and the record count share
// one row (count pinned to the right), with the three filter dropdowns on the
// row below. Desktop keeps search in the middle + count beside the filters.
const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const FilterSelect = GlassSelect;

const ScrollContainer = styled.div`
  /* [STICKY-HEAD 2026-06-26] Owns the table scroll (vertical + horizontal). The DataTable's
     sticky <thead> pins to the top of THIS area (parentScroll), so the column header stays
     just under the frozen toolbar while the rows scroll. The bottom pad clears the floating
     dock at the very end of the scroll so the last row stays above the pill. */
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: scroll; /* [SCROLLBAR 2026-07-08] always show the vertical scroller on desktop */
  padding-bottom: 88px;

  /* [PHONE-LAYOUT 2026-10-23, user] phones: as tall as its rows (no box of its own to scroll up / down in) — it only
     swipes sideways; the page scrolls (Container). The 88 px end pad still lifts the last row above the dock. The
     Description shows ALL its text, wrapped in its column (a phone has no hover for the "…" cut text's tooltip). */
  @media (max-width: 768px) {
    flex: none;
    min-height: 0;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: auto;
    -webkit-overflow-scrolling: touch;

    .desc-text { white-space: normal !important; overflow: visible !important; text-overflow: clip !important; overflow-wrap: anywhere; }
  }
`;


/* [LIQUID-GLASS 2026-11-02, user] a small glass button: red glass under the mouse, a squish and a glow where pressed */
const DeleteBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border: none;
  background-color: transparent;
  color: ${(p) => p.theme.muted};
  font-size: 13px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 8px;
  ${lgPress}
  &:hover {
    color: #dc2626;
    background-color: rgba(220, 38, 38, 0.10);
    box-shadow: inset 0 0 0 1px rgba(220, 38, 38, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.25);
  }

  /* [PHONE-LAYOUT 2026-10-23] phones: a finger-sized button (was 23 x 24 px) */
  @media (max-width: 768px) { min-width: 36px; min-height: 36px; }
`;

const CellInput = styled.input`
  width: 100%;
  /* [CELL-WIDTH 2026-06-26] Shrink to the cell instead of an input's default intrinsic
     (~20-char) width forcing a narrow column wider while editing. */
  min-width: 0;
  /* [SLIM-EDIT 2026-06-25 · taller box] A little vertical padding makes the editor box
     taller and easier to read; an equal NEGATIVE vertical margin cancels it out so the
     cell's content height — and therefore the row height — never changes on edit. The
     INSET box-shadow border traces the (now taller) box without adding any layout height. */
  border: none;
  border-radius: 4px;
  padding: 3px 6px;
  margin: -3px 0;
  font-size: 13px;
  /* [LIQUID-GLASS 2026-11-02] pressed into the glass, with its green editing ring */
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  color: ${(p) => p.theme.text};
  outline: none;
  box-sizing: border-box;
  box-shadow: inset 0 1px 2px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(22,25,23,0.14)')}, inset 0 0 0 1.5px ${(p) => p.theme.green600};
`;

const EditableCell = styled.span`
  cursor: pointer;
  border-bottom: 1px dashed currentColor;
`;

// [CASE-ASSIGN 2026-08-13] In-cell dropdown for Team / Sales-PM Name. Same trick
// InventoryPage's CellSelect uses: the doubled ampersand out-specifies GlassSelect's
// own 130px min-width + vertical padding so the open editor is exactly the size of
// the read-only cell and never widens the column or grows the row. The options list
// is portaled, so it overlays without disturbing layout.
const CellSelect = styled(GlassSelect)`
  && {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

/* ── Helpers ── */

// [EFFECTIVE-MONTH 2026-07-01] The "current month" everywhere here is the app-wide
// effective current month (rolls over at 12:30 PM IST on the 1st, not local midnight).
function getCurrentMonth(): string {
  return effectiveCurrentMonth();
}

function formatMonthLabel(m: string): string {
  const [y, mo] = m.split('-');
  const date = new Date(Number(y), Number(mo) - 1);
  return MONTH_SHORT_YEAR_FMT.format(date);
}

// [MONTH-FULL-NAME] 2026-06-23 — the month *dropdown* shows the full month name
// (e.g. "June 2026") to match the EOM page. The short form (formatMonthLabel) is
// kept for the table month column where width matters.
function formatMonthFull(m: string): string {
  const [y, mo] = m.split('-');
  const date = new Date(Number(y), Number(mo) - 1);
  return MONTH_LONG_YEAR_FMT.format(date);
}

function fmtCurrency(n: number): string {
  return `$${MONEY_FMT.format(n)}`;
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  const date = new Date(d);
  return MONTH_SHORT_DAY_YEAR_UTC_FMT.format(date);
}

/* ── Component ── */

// Self-contained inline-edit field — keeps the in-progress text in its own local
// state so typing never re-renders the whole table. Commits on Enter/blur, cancels
// on Esc; `doneRef` ensures a single commit even though unmounting fires a blur.
const InlineEditInput: React.FC<{
  initialValue: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}> = ({ initialValue, onCommit, onCancel }) => {
  const [draft, setDraft] = useState(initialValue);
  const doneRef = useRef(false);
  const commit = () => { if (doneRef.current) return; doneRef.current = true; onCommit(draft); };
  const cancel = () => { if (doneRef.current) return; doneRef.current = true; onCancel(); };
  return (
    <CellInput
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      }}
    />
  );
};

const RefundsPage: React.FC = () => {
  // Refunds are admin-edit-only: non-admins get a fully read-only table (no
  // website/email edits, no delete) while filters + search keep working.
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const isMobile = useIsMobile(); // [MOBILE 2026-07-07] drives the toolbar reflow + desktop table on phones
  // [CASE-ASSIGN 2026-08-13] Live HubSpot PM/Sales roster behind the Sales-PM Name
  // dropdown — the SAME list the notification bell assigns from. Admins only; nobody
  // else can edit these cells, so nobody else needs the request.
  const roster = useHubSpotRoster(isAdmin);
  const [data, setData] = useState<Refund[]>([]);
  // pageSize: 0 tells the API to return every matching row (pagination removed).
  const [pagination, setPagination] = useState<PaginationMeta>({ page: 1, pageSize: 0, totalRows: 0, totalPages: 0 });
  const [sort, setSort] = useRestorableState<SortConfig>('refunds.sort', { sortBy: 'refund_date', sortOrder: 'desc' });
  const [account, setAccount] = useRestorableState('refunds.account', '');
  // [MULTI-MONTH 2026-07-15] Multi-select month filter. `month` stays derived (''
  // when 0 or 2+ picked) for single-month consumers; `monthsKey` is the stable
  // effect dep — `month` can't tell [Jul,Jun] from [Jul,May] and would skip refetch.
  const [months, setMonths] = useRestorableState<string[]>('refunds.months', [getCurrentMonth()]);
  const month = months.length === 1 ? months[0] : '';
  const monthsKey = months.join(',');
  const [search, setSearch] = useRestorableState('refunds.search', '');
  // Client-side dropdown filters over the already-loaded rows (no refetch). Their
  // options are derived from the loaded data (see teamOptions/assigneeOptions).
  const [teamFilter, setTeamFilter] = useRestorableState('refunds.teamFilter', '');
  const [assigneeFilter, setAssigneeFilter] = useRestorableState('refunds.assigneeFilter', '');
  const [loadFailed, setLoadFailed] = useState(false); // [LOAD-STATES 2026-09-29]
  const [filters, setFilters] = useState<RefundFiltersResponse>({ accounts: [], months: [] });
  const [loading, setLoading] = useState(true);
  const silentRef = useRef(false);
  // [EDIT-RACE 2026-06-26] Tags each load so out-of-order responses are discarded: a
  // slow earlier reload (fetched before a newer inline edit committed) can no longer
  // land last and revert that edit. Mirrors PaymentsPage / InventoryPage.
  const loadRequestIdRef = useRef(0);

  // Inline-edit state for website / email / team cells. Mirrors the PaymentsPage flow.
  type EditField = 'website' | 'email' | 'team' | 'assignee_name';
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<EditField | null>(null);
  // [CELL-PRESS 2026-10-13, user] A dropdown cell (Team, Sales/PM Name) opens on the mouse PRESS — only while no cell is
  // open: an open editor saves on its blur, which that press's own mousedown causes, so then the click path stays and
  // nothing typed is lost. The press is cancelled (no mousedown), so the new list's "click outside" never sees it.
  // Touch / keyboard: the click.
  const cellPressRef = useRef(false);
  const savingRef = useRef<{ id: number; field: EditField } | null>(null);
  // The cell's value when editing began — used to detect a real user change.
  const editOriginalRef = useRef('');

  // [CELL-PRESS 2026-10-13] see cellPressRef above
  const pressOpen = (row: Refund, field: EditField) => ({
    onPointerDown: (e: React.PointerEvent) => {
      cellPressRef.current = isAdmin && editingId === null && isPlainMousePress(e);
      if (cellPressRef.current) { e.preventDefault(); startEdit(row, field); }
    },
    onClick: (e: React.MouseEvent) => {
      if (clickEndsPress(cellPressRef, e)) return; // the press already opened it
      startEdit(row, field);
    },
  });

  const startEdit = (row: Refund, field: EditField) => {
    // Admin-only edit — non-admins never reach here (the cells render plain).
    if (!isAdmin) return;
    // Already editing this exact cell — leave it (the input owns its draft).
    if (editingId === row.id && editingField === field) return;
    const initial = ((field === 'website' ? row.website : field === 'email' ? row.email : field === 'team' ? row.team : row.assignee_name) ?? '') as string;
    setEditingId(row.id);
    setEditingField(field);
    editOriginalRef.current = initial;
  };
  const cancelEdit = () => { setEditingId(null); setEditingField(null); };

  // `committed` is the final text from <InlineEditInput> (Enter / blur). The
  // in-progress text now lives in that child's local state, so the parent only
  // sees it here — which stops the table re-rendering on every keystroke.
  const save = async (committed: string) => {
    if (editingId === null || editingField === null) return;
    const id = editingId;
    const field = editingField;
    if (savingRef.current && savingRef.current.id === id && savingRef.current.field === field) return;
    const val = committed.trim();
    const newVal = val === '' ? null : val;
    // No change → just close. Clicking through cells without editing must not
    // fire a save.
    if (val === editOriginalRef.current.trim()) { cancelEdit(); return; }
    savingRef.current = { id, field };
    cancelEdit();

    // Optimistic UI update — shows the change instantly. Bump the load id so any reload
    // already in flight (e.g. from a previous save) is discarded and can't revert this edit.
    setData((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: newVal } as Refund : r)));
    loadRequestIdRef.current++;

    try {
      await updateRefundField(id, { [field]: val });
      // Silent reload so derived fields refresh without a spinner.
      loadData(true);
      toastSuccess(`${field === 'website' ? 'Website' : field === 'email' ? 'Email' : field === 'team' ? 'Team' : 'Sales/PM Name'} updated`);
    } catch (err) {
      console.error(`Failed to save ${field}:`, err);
      alertDialog({ title: 'Update failed', message: `Failed to update ${field}.`, destructive: true });
      loadData(true);
    } finally {
      savingRef.current = null;
    }
  };

  // [CASE-ASSIGN 2026-08-13] Saves the Team / Sales-PM Name pair — one PUT even when a
  // single dropdown pick changes BOTH columns (picking a name sets its Team; switching
  // Team clears a name that can't sit under it), so the two can't be left disagreeing by
  // a failure between two requests. Same optimistic-paint + stale-load guard as `save`.
  const saveAssignment = async (id: number, fields: { team?: string | null; assignee_name?: string | null }, label: string) => {
    cancelEdit();
    setData((prev) => prev.map((r) => (r.id === id ? ({ ...r, ...fields } as Refund) : r)));
    loadRequestIdRef.current++;
    try {
      await updateRefundField(id, fields);
      // Silent reload so derived fields refresh without a spinner.
      loadData(true);
      toastSuccess(`${label} updated`);
    } catch (err: any) {
      console.error('Failed to save assignment:', err);
      alertDialog({
        title: 'Update failed',
        message: err?.response?.data?.error || `Failed to update ${label}.`,
        destructive: true,
      });
      loadData(true);
    }
  };

  // Load filters
  useEffect(() => {
    fetchRefundFilters().then(setFilters).catch(console.error);
  }, []);

  // Default the month filter to the latest month that has data: the current
  // month once its data lands, otherwise the most recent prior month. Polls so
  // it auto-advances to the current month the moment that data appears, without
  // clobbering an older month the user has deliberately navigated to (it only
  // moves the filter forward when a newer month with data shows up).
  const latestSeenMonthRef = useRef<string | null>(null);
  useEffect(() => {
    let active = true;
    const apply = (months: string[]) => {
      if (!active) return;
      const target = latestDataMonth(months);
      if (latestSeenMonthRef.current === null || target > latestSeenMonthRef.current) {
        latestSeenMonthRef.current = target;
        setMonths([target]);
      }
    };
    const poll = () => fetchRefundFilters().then((f) => apply(f.months)).catch(() => {});
    poll();
    // Skip the tick while the tab is hidden — an idle/background tab shouldn't query.
    const id = setInterval(() => { if (document.hidden) return; poll(); }, 60000);
    return () => { active = false; clearInterval(id); };
  }, []);

  // Reload account list when month changes
  useEffect(() => {
    fetchRefundFilters(month || undefined).then((f) => {
      setFilters((prev) => ({ ...prev, accounts: f.accounts }));
      setAccount('');
    }).catch(console.error);
  }, [month]);

  // Load data
  const loadData = useCallback(async (silent = false) => {
    const reqId = ++loadRequestIdRef.current;
    // [TAB-FAST 2026-07-07] Seed instantly from the SWR GET cache (warmed by the idle
    // prefetch or a prior visit) so a warmed tab paints its rows at once with NO skeleton
    // flash — only a genuinely cold combo flips the skeleton on. Mirrors fetchRefunds's
    // params exactly (''/undefined dropped by the cache-key normaliser) so the key lines up;
    // the background fetch below still runs and revalidates. A cache miss is a safe no-op.
    const seed = silent ? undefined : peekCache<{ data: Refund[]; pagination: PaginationMeta }>('/refunds', monthsParams({ page: 1, pageSize: 0, account, months: months.length ? months : undefined, search }));
    if (!silent) {
      if (seed) { setData(seed.data); setPagination(seed.pagination); setLoading(false); }
      else setLoading(true);
    }
    silentRef.current = silent;
    try {
      const res = await fetchRefunds({
        page: 1,
        pageSize: 0,
        account,
        months: months.length ? months : undefined,
        search,
      });
      // Stale/out-of-order response (a newer load or inline edit started after this one)
      // — drop it so it can't overwrite the table and revert a newer edit.
      if (reqId !== loadRequestIdRef.current) return;
      setData(res.data);
      setPagination(res.pagination);
      setLoadFailed(false); // [LOAD-STATES 2026-09-29]
    } catch (err) {
      if (reqId !== loadRequestIdRef.current) return;
      console.error('Failed to load refunds:', err);
      // [LOAD-STATES 2026-09-29, user] A quiet refresh keeps what is on screen. A real load that
      // failed shows the countdown card — unless a cached copy of THESE filters is already showing.
      if (!silent) { setLoadFailed(true); if (!seed) setData([]); }
    } finally {
      // [SKELETON-FLASH 2026-06-26] Only the CURRENT (winning) request may clear the
      // skeleton. A superseded load (reqId mismatch → the early returns above discard its
      // data) must NOT flip loading=false, or the empty-state ("No refunds found") flashes
      // between the skeleton and the winning load's data. Matches Payments/Inventory/New Sales.
      if (reqId === loadRequestIdRef.current) setLoading(false);
    }
    // NOTE: `sort` is intentionally NOT a dependency — column-header sorting is
    // done client-side (see `sorted` below) so it re-orders in place without a
    // refetch/skeleton. Only filter changes reload from the server.
  }, [account, monthsKey, search]);

  useEffect(() => { loadData(); }, [loadData]);

  // [REALTIME] 2026-06-23 — silently refresh when refunds change (any source).
  useRealtime(['refunds'], () => loadData(true));

  // Confirm-delete dialog state. We keep the row pending until the user
  // clicks Delete so the message can reference the website name.
  const [pendingDelete, setPendingDelete] = useState<{ id: number; label: string } | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const requestDelete = (id: number, label: string) => setPendingDelete({ id, label });

  const cancelDelete = () => { if (!deleteBusy) setPendingDelete(null); };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    try {
      await deleteRefund(pendingDelete.id);
      setPendingDelete(null);
      loadData(true);
      toastSuccess('Refund deleted');
    } catch (err) {
      console.error('Failed to delete refund:', err);
      alertDialog({ title: 'Delete failed', message: 'Failed to delete refund.', destructive: true });
    } finally {
      setDeleteBusy(false);
    }
  };

  const baseColumns: Column[] = useMemo(() => [
    { key: 'refund_date', label: 'Refund Date', sortable: true, width: '100px',
      render: (v: any) => fmtDate(v) },
    { key: 'website', label: 'Website', sortable: true, width: '150px' },
    { key: 'email', label: 'Email', sortable: true, width: '180px' },
    { key: 'client_name', label: 'Client', sortable: true, width: '120px' },
    { key: 'refund_amount', label: 'Amount', sortable: true, align: 'right', width: '100px',
      render: (v: any) => (
        <span style={{ color: '#dc2626', fontWeight: 600 }}>
          -{fmtCurrency(Number(v) || 0)}
        </span>
      ) },
    { key: 'account_name', label: 'Account', sortable: true, width: '120px' },
    { key: 'charge_id', label: 'Charge ID', sortable: true, width: '140px', headerAlign: 'center' },
    { key: 'payment_date', label: 'Payment Date', sortable: true, width: '100px',
      render: (v: any) => fmtDate(v) },
    { key: 'object_type', label: 'Type', sortable: true, width: '80px' },
    { key: 'description', label: 'Description', sortable: true, width: '180px', headerAlign: 'center',
      render: (v: any) => v ? (
        <span title={v} className="desc-text" /* [PHONE-LAYOUT 2026-10-23] wraps on phones (ScrollContainer) */ style={{ maxWidth: '180px', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {v}
        </span>
      ) : '—' },
    { key: 'invoice_number', label: 'Invoice #', sortable: true, width: '120px', headerAlign: 'center',
      render: (v: any) => v || '—' },
    { key: 'refund_id', label: 'Refund ID', sortable: true, width: '140px', headerAlign: 'center' },
    { key: 'month', label: 'Month', sortable: true, width: '90px',
      render: (v: any) => v ? formatMonthLabel(v.substring(0, 7)) : '—' },
    { key: 'month_status', label: 'Month Status', sortable: true, width: '100px', align: 'center',
      render: (v: any) => v || '—' },
    { key: 'team', label: 'Team', sortable: true, width: '80px',
      render: (v: any) => v || '—' },
    { key: 'assignee_name', label: 'Sales/PM Name', sortable: true, width: '130px',
      render: (v: any) => v || '—' },
    // [RD-ROW-TAG 2026-10-01, user] Team ROW refunds carry the ROW tag — stored by the server on the row.
    { key: 'row_tag', label: 'Tag', width: '80px', align: 'center',
      render: (v: any) => (v === 'ROW' ? <RowTagBadge /> : '—') },
    { key: 'actions', label: 'Delete', width: '70px', align: 'center',
      render: (_v: any, row: any) => (
        <DeleteBtn onClick={() => requestDelete(row.id, row.website || row.email || `#${row.id}`)} title="Delete refund">
          <i className="fas fa-trash-alt"></i>
        </DeleteBtn>
      ) },
  ], []);

  // Overlay inline-edit behaviour onto the website + email columns. Rebuilt
  // every render so the render closures see the live editing state.
  const columns: Column[] = baseColumns
    // Non-admins: drop the Delete column entirely (view-only).
    .filter((col) => isAdmin || col.key !== 'actions')
    .map((col) => {
    if (col.key === 'website') {
      return { ...col, render: (val: any, row: any) => {
        // Admin-edit-only: non-admins get a plain, view-only cell.
        if (!isAdmin) return val || '—';
        const isEditing = editingId === row.id && editingField === 'website';
        if (isEditing) {
          return (
            <InlineEditInput
              initialValue={editOriginalRef.current}
              onCommit={save}
              onCancel={cancelEdit}
            />
          );
        }
        return (
          <EditableCell onClick={() => startEdit(row, 'website')} title="Click to edit">
            {val || '—'}
          </EditableCell>
        );
      }};
    }
    if (col.key === 'email') {
      return { ...col, render: (val: any, row: any) => {
        // Admin-edit-only: non-admins get a plain, view-only cell.
        if (!isAdmin) return val || '—';
        const isEditing = editingId === row.id && editingField === 'email';
        if (isEditing) {
          return (
            <InlineEditInput
              initialValue={editOriginalRef.current}
              onCommit={save}
              onCancel={cancelEdit}
            />
          );
        }
        return (
          <EditableCell onClick={() => startEdit(row, 'email')} title="Click to edit">
            {val || '—'}
          </EditableCell>
        );
      }};
    }
    // [CASE-ASSIGN 2026-08-13] Team is a fixed PM / Sales dropdown (was free text).
    // Switching it drops a Sales-PM Name that can't sit under the new team, so the two
    // columns can never end up contradicting each other.
    if (col.key === 'team') {
      return { ...col, render: (val: any, row: any) => {
        // Admin-edit-only: non-admins get a plain, view-only cell.
        if (!isAdmin) return val || '—';
        const isEditing = editingId === row.id && editingField === 'team';
        if (isEditing) {
          const current = (row.team ?? '') as string;
          return (
            <CellSelect liquidList
              defaultOpen
              value={current}
              onChange={(e) => {
                const next = e.target.value;
                if (next === current) { cancelEdit(); return; }
                const name = (row.assignee_name ?? '') as string;
                const keepName = next !== '' && name !== '' && roster.fitsTeam(name, next as CaseTeam);
                saveAssignment(
                  row.id,
                  keepName ? { team: next || null } : { team: next || null, assignee_name: null },
                  'Team',
                );
              }}
              onClose={cancelEdit}
            >
              <option value="">—</option>
              <option value="PM">PM</option>
              <option value="Sales">Sales</option>
              {/* A legacy free-text value (pre-dropdown) stays visible as the current
                  selection instead of the cell reading blank; picking PM/Sales replaces it. */}
              {current !== '' && current !== 'PM' && current !== 'Sales' && (
                <option value={current}>{current}</option>
              )}
            </CellSelect>
          );
        }
        return (
          <EditableCell {...pressOpen(row, 'team')} title="Click to edit">
            {val || '—'}
          </EditableCell>
        );
      }};
    }
    // [CASE-ASSIGN 2026-08-13] One combined, alphabetical list of every current HubSpot
    // PM and Sales person (was free text). Picking someone also sets Team from their
    // HubSpot role — except for people who are BOTH, where the role can't decide it and
    // whatever Team the row already carries is left alone.
    if (col.key === 'assignee_name') {
      return { ...col, render: (val: any, row: any) => {
        // Admin-edit-only: non-admins get a plain, view-only cell.
        if (!isAdmin) return val || '—';
        const isEditing = editingId === row.id && editingField === 'assignee_name';
        if (isEditing) {
          const current = (row.assignee_name ?? '') as string;
          // A stored name that's no longer on the roster (someone who has left) is
          // prepended so the cell still shows the truth rather than an empty box.
          const names = current !== '' && !roster.names.includes(current)
            ? [current, ...roster.names]
            : roster.names;
          return (
            <CellSelect liquidList
              defaultOpen
              value={current}
              onChange={(e) => {
                const next = e.target.value;
                if (next === current) { cancelEdit(); return; }
                const fields: { team?: string | null; assignee_name?: string | null } = {
                  assignee_name: next || null,
                };
                const derived = next === '' ? null : roster.teamOf(next);
                if (derived && derived !== row.team) fields.team = derived;
                saveAssignment(row.id, fields, 'Sales/PM Name');
              }}
              onClose={cancelEdit}
            >
              <option value="">—</option>
              {names.map((nm) => (
                <option key={nm} value={nm}>{nm}</option>
              ))}
            </CellSelect>
          );
        }
        return (
          <EditableCell {...pressOpen(row, 'assignee_name')} title="Click to edit">
            {val || '—'}
          </EditableCell>
        );
      }};
    }
    return col;
  });

  const handleSort = (col: string) => {
    setSort((prev) => ({
      sortBy: col,
      sortOrder: prev.sortBy === col && prev.sortOrder === 'desc' ? 'asc' : 'desc',
    }));
  };

  // Distinct, alphabetised Team / Sales-PM values present in the loaded rows —
  // these populate the two filter dropdowns.
  const teamOptions = useMemo(
    () => Array.from(new Set(data.map((r) => r.team).filter((t): t is string => !!t))).sort((a, b) => a.localeCompare(b)),
    [data],
  );
  const assigneeOptions = useMemo(
    () => Array.from(new Set(data.map((r) => r.assignee_name).filter((a): a is string => !!a))).sort((a, b) => a.localeCompare(b)),
    [data],
  );

  // [MONTH-DROPDOWN 2026-07-01] Always offer the CURRENT effective month (and whatever
  // month is selected) in the dropdown, even when it has no data yet — otherwise the
  // select can't display the current month and visually falls back to the newest month
  // that DOES have data (e.g. showing "June" while the filter is really on an empty July,
  // 0 records). filters.months is newest-first and the current month is the newest, so
  // prepending the pinned months keeps that order. Deduped so a month with data isn't listed twice.
  const monthOptions = useMemo(() => {
    const pinned = [effectiveCurrentMonth(), month].filter((m) => m && !filters.months.includes(m));
    return Array.from(new Set([...pinned, ...filters.months]));
  }, [filters.months, month]);

  // Client-side Team/Sales-PM filter + sort over the already-loaded rows (the fetch
  // returns every row, pageSize:0), so changing a filter or clicking a column header
  // updates in place — no refetch, no skeleton flash. Amounts (numbers) sort
  // numerically; text + ISO date/month strings sort lexicographically (ISO strings
  // are chronological).
  const sorted = useMemo(() => {
    let rows = data;
    if (teamFilter) rows = rows.filter((r) => r.team === teamFilter);
    if (assigneeFilter) rows = rows.filter((r) => r.assignee_name === assigneeFilter);
    if (!sort.sortBy) return rows;
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    const k = sort.sortBy as keyof Refund;
    return [...rows].sort((a, b) => {
      const av = a[k];
      const bv = b[k];
      if (typeof av === 'number' || typeof bv === 'number') {
        return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
      }
      return (av ?? '').toString().localeCompare((bv ?? '').toString()) * dir;
    });
  }, [data, sort, teamFilter, assigneeFilter]);

  const resetPage = () => setPagination((prev) => ({ ...prev, page: 1 }));

  // Extracted so both toolbar layouts (desktop inline / mobile SearchRow) reuse
  // the exact same search field without duplicating its onChange.
  const searchField = (
    <SearchBar inline liquid placeholder="Search refunds..." value={search} onChange={(v) => { setSearch(v); setMonths(v ? [] : [getCurrentMonth()]); if (v) setAccount(''); setTeamFilter(''); setAssigneeFilter(''); resetPage(); }} />
  );

  return (
    <Container>
      {/* [RD-PILL 2026-09-10] data-rd-toolbar: RefundsDisputesPage holds this block still while the table below slides. */}
      <FrozenTop data-rd-toolbar>
      <Toolbar>
        {/* [RD-PILL 2026-09-10] Refunds | Disputes pill (was a title dropdown). Picking one slides
            to that page (see RefundsDisputesPage) — both stay mounted, so each keeps its own filters. */}
        <SectionSwitcher liquid current="refunds" />
        {/* [MOBILE 2026-07-07] Phones: search + record count share one row
            (SearchRow); the 3 dropdowns drop to their own row (Filters grid).
            Desktop: search fills the middle, count sits with the filters. */}
        {isMobile ? (
          <SearchRow>
            {searchField}
            <RecordCount>{loadFailed && data.length === 0 ? 'Not loaded' : `${sorted.length.toLocaleString()} records fetched`}</RecordCount>
          </SearchRow>
        ) : searchField}
        <Filters>
          {!isMobile && <RecordCount>{loadFailed && data.length === 0 ? 'Not loaded' : `${sorted.length.toLocaleString()} records fetched`}</RecordCount>}
          {/* [SINGLE-MONTH 2026-07-28] One month at a time (multi-select withdrawn).
              State stays a string[] of length 1 so nothing downstream changes. */}
          <FilterSelect liquid
            value={months[0] || ''}
            onChange={(e) => { setMonths([e.target.value]); setTeamFilter(''); setAssigneeFilter(''); resetPage(); }}
          >
            {Array.from(new Set([...months, ...monthOptions]))
              .sort((a, b) => b.localeCompare(a))
              .map((m) => (
                <option key={m} value={m}>{formatMonthFull(m)}</option>
              ))}
          </FilterSelect>
          {/* Team + Sales/PM filters — client-side over the loaded rows; options
              reflect the data currently in view. Reset when the month/search changes. */}
          <FilterSelect liquid value={teamFilter} onChange={(e) => { setTeamFilter(e.target.value); resetPage(); }}>
            <option value="">All Teams</option>
            {teamOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </FilterSelect>
          <FilterSelect liquid value={assigneeFilter} onChange={(e) => { setAssigneeFilter(e.target.value); resetPage(); }}>
            <option value="">All Sales/PM</option>
            {assigneeOptions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </FilterSelect>
        </Filters>
      </Toolbar>
      </FrozenTop>

      {/* [RD-PILL 2026-09-10] data-rd-table: the only part that slides on a Refunds/Disputes switch. */}
      <ScrollContainer data-rd-table>
        {loading ? (
          <TableSkeleton columns={columns} />
        ) : loadFailed && data.length === 0 ? (
          <LoadFailed what="refunds" onRetry={() => loadData()} />
        ) : data.length === 0 ? (
          // [LOAD-STATES 2026-09-29, user] Say what is empty and offer the fix.
          search
            ? <EmptyState icon="search" title={`No refunds match “${search}”`} hint="Check the spelling, or clear the search to see every refund."
                action={{ label: 'Clear search', onClick: () => { setSearch(''); setMonths([getCurrentMonth()]); resetPage(); } }} />
            : <EmptyState title={`No refunds${months.length === 1 ? ` in ${formatMonthFull(months[0])}` : ''}`}
                hint="Pick another month or account to see older refunds." />
        ) : (
          <DataTable
            parentScroll
            columns={columns}
            data={sorted}
            sort={sort}
            onSort={handleSort}
            // [MOBILE 2026-07-07] Wide refunds grid — keep the SAME desktop table on
            // phones (no card view); it scrolls horizontally inside ScrollContainer
            // so the full grid reads exactly like the website.
            disableMobileCards
            // [COL-RESIZE-ALL 2026-09-29, user] Drag a header's edge to resize (same as Payments).
            resizeStorageKey={`dt-refunds-colw:${user?.id ?? 'anon'}`}
            // [LOAD-STATES 2026-09-29, user] Rows exist but the Team / person filters hide them all.
            highlight={search} // [SEARCH-HIGHLIGHT 2026-09-29]
            empty={{ icon: 'search', title: 'No refunds for this team or person', hint: 'Clear the filters to see every refund in this month.', action: { label: 'Clear filters', onClick: () => { setTeamFilter(''); setAssigneeFilter(''); } } }}
          />
        )}
      </ScrollContainer>

      <ConfirmDialog liquid
        open={!!pendingDelete}
        title="Delete refund?"
        message={
          <>
            Move the refund for <strong>{pendingDelete?.label}</strong> to the trash?
            You can restore it later from the trash icon in the top bar.
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        busy={deleteBusy}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </Container>
  );
};

export default RefundsPage;
